import { useMemo } from "react";

import { UNIT_TYPE_VALUES, unitCategories, unitsJson } from "@corca/data";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { mapUnitToSetChildren } from "@/components/doc/right-sidebar/active-node-style/fields/unitUtils";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { setNodeChildren } from "@/components/suggestions/utils";

import { UnitsDropdown } from "./UnitsDropdown";

import type { NodeWithUnit } from "./types";
import type { ItemWithUnits } from "./UnitsDropdown";

type Props = {
  node: NodeWithUnit;
};

export const UnitTypeSelector = observer(function UnitTypeSelector({ node }: Props) {
  const { doc, unitNode } = node;
  const t = useTranslations("sidebar.activeNode");

  const items: ItemWithUnits[] = useMemo(() => {
    const unitItems = UNIT_TYPE_VALUES.map((unitType) => {
      const units = unitsJson.filter((unit) => unitCategories(unit).includes(unitType));
      const unitsWithType = units.map((unit) => ({ ...unit, unitType }));

      return {
        title: t(`unit.semanticType.types.${unitType}`),
        active: unitNode?.unitType === unitType,
        units: unitsWithType,
        onClick: () => {
          const [unit] = units;

          if (!unit) return;

          doc.transact(() => {
            setNodeChildren(doc, node, mapUnitToSetChildren({ ...unit, unitType }));
          }, "UnitTypeSelector:unitTypeChange");
        },
      };
    });

    return [
      {
        title: t(`title.${node.type}`),
        active: !unitNode,
        units: [],
        onClick: () => {
          if (!unitNode) return;
          doc.transact(() => {
            doc.removeNode(unitNode);
          }, "UnitTypeSelector:unitTypeChange");
        },
      },
      ...unitItems,
    ];
  }, [doc, node, unitNode, t]);

  const title = unitNode
    ? t(`unit.semanticType.types.${unitNode.unitType ?? "custom"}`)
    : t(`title.${node.type}`);

  return (
    <UnitsDropdown
      node={node}
      items={items}
      header={() => (
        <Sidebar.SectionTitle className="mb-1">{t("unit.semanticType.title")}</Sidebar.SectionTitle>
      )}
      toggleButtonComponent={
        <div className="flex items-center font-mono text-xxs uppercase tracking-tight text-gray-500">
          {title}
          <Icon name="ChevronDown" />
        </div>
      }
      itemsClassName="min-w-[350px]"
    />
  );
});
